import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowUpRight } from "lucide-react";

const INDUSTRIES = [
  {
    name: "Higher Education",
    desc: "Universities, Colleges, EdTech Companies",
    img: "https://images.pexels.com/photos/31656148/pexels-photo-31656148.jpeg",
    color: "saavi-blue",
  },
  {
    name: "Healthcare",
    desc: "Hospitals, Clinics, Healthcare Brands",
    img: "https://images.pexels.com/photos/33812025/pexels-photo-33812025.jpeg",
    color: "saavi-red",
  },
  {
    name: "Real Estate",
    desc: "Developers, Builders, Property Consultants",
    img: "https://images.pexels.com/photos/33397572/pexels-photo-33397572.jpeg",
    color: "saavi-green",
  },
  {
    name: "E-Commerce",
    desc: "Retail & D2C Brands",
    img: "https://images.pexels.com/photos/6170176/pexels-photo-6170176.jpeg",
    color: "saavi-blue",
  },
  {
    name: "Hospitality",
    desc: "Hotels, Resorts, Travel Companies",
    img: "https://images.pexels.com/photos/29870245/pexels-photo-29870245.jpeg",
    color: "saavi-red",
  },
  {
    name: "Professional Services",
    desc: "Consultants, Coaches, Financial Services",
    img: "https://images.pexels.com/photos/7580763/pexels-photo-7580763.jpeg",
    color: "saavi-green",
  },
];

const WHY_DIGITAL = [
  "90%+ buyers research online before purchasing",
  "Digital marketing offers measurable ROI",
  "Reach the right audience at the right time",
  "Build brand trust and authority",
  "Generate leads 24/7",
];

const BENEFITS = [
  { label: "Higher Revenue", color: "text-saavi-blue" },
  { label: "Better Brand Visibility", color: "text-saavi-red" },
  { label: "Faster Customer Acquisition", color: "text-saavi-green" },
  { label: "Improved Customer Retention", color: "text-saavi-blue" },
  { label: "Business Scalability", color: "text-saavi-red" },
];

export default function Industries() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="Industries We Serve"
        title={<>We help <span className="text-saavi-blue">ambitious brands</span> grow across sectors.</>}
        description="Deep domain experience across education, healthcare, real estate, e-commerce, hospitality and professional services."
      />

      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} variant="fade-up" delay={i * 90}>
                <article
                  data-testid={`industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`card-hover lift-on-hover ai-card group relative overflow-hidden border border-border rounded-md bg-card hover:border-${ind.color} h-full`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img src={ind.img} alt={ind.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/40 to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl transition-colors duration-300 group-hover:text-saavi-blue">{ind.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{ind.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad border-b border-border">
        <div className="container-saavi grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono-overline text-saavi-red mb-4">Why Invest in Digital Marketing</div>
            <h2 className="text-4xl md:text-5xl">Your customers are already <span className="text-saavi-blue">online.</span></h2>
          </div>
          <div className="lg:col-span-7 space-y-3">
            {WHY_DIGITAL.map((line) => (
              <div key={line} className="flex items-start gap-3 border border-border rounded-md p-5 bg-card">
                <CheckCircle2 className="h-5 w-5 text-saavi-green flex-shrink-0 mt-0.5" />
                <p className="text-base font-semibold leading-snug">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-saavi">
          <div className="font-mono-overline text-saavi-green mb-4">Benefits</div>
          <h2 className="text-4xl md:text-5xl max-w-3xl mb-12">What you can expect when you partner with us.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {BENEFITS.map((b) => (
              <div key={b.label} className="border border-border rounded-md p-6 bg-card">
                <div className={`text-3xl font-extrabold ${b.color}`}>✓</div>
                <p className="mt-3 text-base font-semibold">{b.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 flex justify-center">
            <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2">Start Growing Today <ArrowUpRight className="h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
